import _compact from "lodash/compact";
import { v4 as uuidv4 } from "uuid";
import { send } from "./GoogleAnalyticsClient";
import { State } from "./state/selectors";
import {
    GA_TEXT_CONSTS as TEXT_CONSTS,
    GTM_EVENT as GTMEvent,
    GA_CATEGORIES as CATEGORIES,
    CategoriesString,
} from "../../../constants";
import { GAProduct, GAProductDimensions, GTMEventType, RootState } from "../../../models";
import { Package } from "../../../models/state";
import { getSelectedPackageId, getPackageFromId, getTVPackagesToShow } from "../fwa/state/selectors";

const PRODUCT_CATEGORY = CATEGORIES.SUBSCRIPTION_FWA;
const WIFIX_PRODUCT_CATEGORY = CATEGORIES.MODEM_WIFIX;
const wifix_cpid = 1681;
const BRAND = "Telia";

export const getFwaCategory = (type: string | undefined, fallbackCategory: CategoriesString = PRODUCT_CATEGORY) => {
    const isTVPackage = type?.includes("-tv") || fallbackCategory === CATEGORIES.SUBSCRIPTION_FWA_TV;
    if (type?.includes("cabin") || fallbackCategory === CATEGORIES.SUBSCRIPTION_FWA_CABIN) {
        return isTVPackage ? CATEGORIES.SUBSCRIPTION_FWA_CABIN_TV : CATEGORIES.SUBSCRIPTION_FWA_CABIN;
    }
    return isTVPackage ? CATEGORIES.SUBSCRIPTION_FWA_TV : fallbackCategory;
};

const getListOfProductNames = (products: Package[]): string =>
    products.map((product) => product.originalName).toString();

const getDimensionsFor = (eventType: GTMEventType, products: Package[]): GAProductDimensions => {
    switch (eventType) {
        case GTMEvent.IMPRESSION:
            return {};
        case GTMEvent.ADD_TO_CART:
            return {
                dimension1: "Billing",
                dimension2: "Subscription/Broadband/Fixed wireless|Service/Delivery",
                dimension3: "Acquisition",
            };
        case GTMEvent.DELIVERY:
            return {
                dimension2: "Subscription/Broadband/Fixed wireless|Service/Delivery",
                dimension74: getListOfProductNames(products),
                dimension80: "Families",
            };
        default:
            return {
                dimension1: "Billing",
                dimension75: "Levert på døren",
                dimension2: "Subscription/Broadband/Fixed wireless|Service/Delivery",
                dimension74: getListOfProductNames(products),
                dimension80: "Families",
            };
    }
};

const productWithDimensions =
    (dimensions: GAProductDimensions, category: CategoriesString = PRODUCT_CATEGORY) =>
    (product: Package, idx: number): GAProduct => {
        const id = product.id;
        const isWIFIX = id === wifix_cpid;
        const productCategory = getFwaCategory(product.type, category);

        return {
            brand: BRAND,
            category: isWIFIX ? WIFIX_PRODUCT_CATEGORY : productCategory,
            id: `${id}`,
            name: product.originalName,
            price: product.monthlyPrice.discountPrice * 12 + product.installationPrice.discountPrice,
            variant: product.originalName,
            list: productCategory,
            position: idx + 1,
            quantity: 1,
            ...dimensions,
        };
    };

export const trackProductClick = (
    salesPackage: Package,
    index: number,
    category: CategoriesString = PRODUCT_CATEGORY,
): Promise<unknown> => {
    const productCategory = getFwaCategory(salesPackage.type, category);
    return send({
        event: GTMEvent.PRODUCT_CLICK,
        ecommerce: {
            click: {
                actionField: { list: productCategory },
                products: [
                    {
                        brand: BRAND,
                        category: productCategory,
                        id: `${salesPackage.id}`,
                        name: salesPackage.originalName,
                        price:
                            salesPackage.monthlyPrice.discountPrice * 12 + salesPackage.installationPrice.discountPrice,
                        variant: salesPackage.originalName,
                        position: index + 1,
                        quantity: 1,
                    },
                ],
            },
        },
    });
};

export const trackFirstLoad = (
    packages: Package[],
    category: CategoriesString = PRODUCT_CATEGORY,
): Promise<unknown> => {
    const productsImpressions = packages.map(productWithDimensions({}, category));

    return send({
        event: GTMEvent.IMPRESSION,
        ecommerce: {
            currencyCode: "NOK",
            impressions: productsImpressions,
        },
    });
};

export const trackAddToCart = (
    packages: Package[],
    category: CategoriesString = PRODUCT_CATEGORY,
): Promise<unknown> => {
    const dimensions = getDimensionsFor(GTMEvent.ADD_TO_CART, packages);
    const products = productWithDimensions(dimensions, category);
    const productsToCart = packages.map(products);

    return send({
        event: GTMEvent.ADD_TO_CART,
        ecommerce: {
            currencyCode: "NOK",
            add: {
                products: productsToCart,
            },
        },
    });
};

const firstYearPriceReducer = (accumulated: number, currentProduct: Package): number =>
    accumulated + currentProduct.monthlyPrice.discountPrice * 12 + currentProduct.installationPrice.discountPrice;

export const trackPurchase = (
    salesPackages: (Package | undefined)[],
    additionalProduct: Package[],
    category: CategoriesString = PRODUCT_CATEGORY,
): Promise<unknown> => {
    const shippingPrice = 0;
    const packages = _compact(salesPackages);
    const extraProduct: GAProduct = {
        name: "Levert på døren",
        id: GTMEvent.DELIVERY,
        price: shippingPrice,
        brand: BRAND,
        category: CATEGORIES.DELIVERY,
        quantity: 1,
        ...getDimensionsFor(GTMEvent.DELIVERY, packages),
    };

    const transactionId = `fwa-${uuidv4()}`;
    const totalPriceMain = packages.reduce(firstYearPriceReducer, 0);
    const totalPriceAddon = additionalProduct.reduce(firstYearPriceReducer, 0);
    const totalPrice = totalPriceMain + totalPriceAddon + shippingPrice;
    const totalVAT = totalPrice * 0.2;

    const dimensions = getDimensionsFor(GTMEvent.TRANSACTION, packages);
    const products = productWithDimensions(dimensions, category);
    const productsTransaction = [...packages, ...additionalProduct].map(products);

    return send({
        event: GTMEvent.TRANSACTION,
        ecommerce: {
            purchase: {
                actionField: {
                    id: transactionId, // Transaction ID. Required for purchases and refunds.
                    affiliation: TEXT_CONSTS.AFFILIATION,
                    revenue: totalPrice, // Total transaction value (incl. tax and shipping)
                    tax: totalVAT,
                },
                products: [...productsTransaction, extraProduct],
            },
        },
    });
};

export const trackPurchaseFromState = (state: State): Promise<unknown> => {
    const rootState = state as unknown as RootState;
    const selectedPackageId = getSelectedPackageId(rootState);
    const selectedPackage = getPackageFromId(rootState, selectedPackageId);
    const isTVPackage =
        (selectedPackage?.type || "").includes("-tv") ||
        getTVPackagesToShow(rootState).some((pkg) => pkg.id === selectedPackageId);
    return trackPurchase(
        [selectedPackage],
        [],
        getFwaCategory(selectedPackage?.type, isTVPackage ? CATEGORIES.SUBSCRIPTION_FWA_TV : PRODUCT_CATEGORY),
    );
};
